import { useTranslation } from "react-i18next";
import {
  Box,
  PlugZap,
} from "lucide-react";
import { ScrollReveal } from "./ScrollReveal";

interface Tool {
  name: string;
  description: string;
}

export const COMPATIBLE_TOOLS = [
  { key: "opencode", name: "OpenCode", logoSrc: "/tool-logos/opencode.svg" },
  { key: "claude code", name: "Claude Code", logoSrc: "/tool-logos/claude.svg" },
  { key: "hermes", name: "Hermes", logoSrc: "/tool-logos/hermes.svg" },
  { key: "codex", name: "Codex", logoSrc: "/tool-logos/codex.svg" },
  { key: "openclaw", name: "OpenClaw", logoSrc: "/tool-logos/openclaw.svg" },
] as const;

function getToolLogo(name: string): string | undefined {
  const normalized = name.toLowerCase();
  return COMPATIBLE_TOOLS.find((tool) => tool.key === normalized)?.logoSrc;
}

export function ToolLogoStrip({ compact = false }: { compact?: boolean }) {
  return (
    <div
      className={[
        "flex flex-wrap items-center gap-2",
        compact ? "justify-start lg:justify-center" : "justify-center",
      ].join(" ")}
      aria-label="Compatible tools"
    >
      {COMPATIBLE_TOOLS.map((tool) => (
        <span
          key={tool.key}
          title={tool.name}
          className={[
            "inline-flex items-center justify-center gap-2 rounded-[var(--radius-md)] border border-[var(--color-line)] bg-[var(--color-panel)] font-bold text-[var(--color-ink-mid)] shadow-[0_10px_24px_rgba(60,41,112,0.05)]",
            compact ? "h-10 w-10" : "min-h-10 px-3 text-[12.5px]",
          ].join(" ")}
        >
          <img
            src={tool.logoSrc}
            alt=""
            aria-hidden="true"
            className={compact ? "h-5 w-5" : "h-5 w-5"}
            loading="lazy"
            decoding="async"
          />
          <span className={compact ? "sr-only" : undefined}>{tool.name}</span>
        </span>
      ))}
    </div>
  );
}

export function WorksWithSection() {
  const { t } = useTranslation();

  const tools = t("landing.worksWithSection.tools", {
    returnObjects: true,
  }) as unknown as Tool[];
  const protocols = t("landing.worksWithSection.protocols", {
    returnObjects: true,
  }) as unknown as Tool[];

  return (
    <section
      id="tools"
      aria-labelledby="works-with-title"
      className="section-anchor border-b border-[var(--color-line)] bg-[var(--color-panel)] py-16 md:min-h-[520px] md:py-24"
    >
      <div className="page-container flex min-h-[inherit] items-center">
        <div className="grid w-full gap-8 lg:grid-cols-[0.42fr_1fr] lg:items-center lg:gap-12">
          <ScrollReveal variant="section">
            <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-[var(--color-primary-deep)]">
              {t("landing.worksWithSection.eyebrow")}
            </p>
            <h2
              id="works-with-title"
              className="mt-2 text-[28px] font-bold leading-9 tracking-[0] text-[var(--color-ink-strong)] text-balance md:text-[40px] md:leading-[1.08]"
            >
              {t("landing.worksWithSection.title")}
            </h2>
            <p className="mt-4 max-w-[42ch] text-[15px] leading-6 text-[var(--color-text)] text-pretty md:text-[16px] md:leading-7">
              {t("landing.worksWithSection.body")}
            </p>
          </ScrollReveal>

          <ScrollReveal variant="visual" delay={120} className="rounded-[var(--radius-xl)] border border-[var(--color-line)] bg-[var(--color-panel-subtle)] p-4 shadow-[0_18px_48px_rgba(60,41,112,0.07)] md:p-5">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
              {tools.map((tool, index) => {
                const logoSrc = getToolLogo(tool.name);
                return (
                  <ScrollReveal
                    as="article"
                    key={tool.name}
                    delay={180 + index * 70}
                    variant="item"
                    className="lift flex min-h-[156px] flex-col justify-between rounded-[var(--radius-lg)] border border-[var(--color-line)] bg-[var(--color-panel)] p-4"
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[var(--radius-md)] border border-[var(--color-line)] bg-[var(--color-page-soft)] text-[var(--color-primary)]">
                        {logoSrc ? (
                          <img src={logoSrc} alt="" aria-hidden="true" className="h-6 w-6" loading="lazy" decoding="async" />
                        ) : (
                          <Box className="h-4 w-4" aria-hidden="true" />
                        )}
                      </div>
                      <h3 className="text-[14px] font-bold leading-5 text-[var(--color-ink-strong)]">
                        {tool.name}
                      </h3>
                    </div>
                    <p className="mt-3 text-[12.5px] leading-5 text-[var(--color-muted)] text-pretty">
                      {tool.description}
                    </p>
                  </ScrollReveal>
                );
              })}
            </div>

            <div className="mt-5 grid min-h-[82px] gap-3 border-t border-[var(--color-line)] pt-5 md:grid-cols-[minmax(0,1fr)_auto] md:items-center">
              <div className="flex min-w-0 items-center gap-3">
                <div className="num flex h-12 w-14 shrink-0 items-center justify-center rounded-[var(--radius-md)] border border-[var(--color-line-strong)] bg-white text-[20px] font-bold text-[var(--color-primary-deep)]">
                  {t("landing.worksWithSection.protocolsCount")}
                </div>
                <div className="min-w-0">
                  <p className="flex items-center gap-2 text-[13px] font-bold text-[var(--color-ink-strong)]">
                    <PlugZap className="h-3.5 w-3.5 text-[var(--color-primary)]" aria-hidden="true" />
                    {t("landing.worksWithSection.protocolsLabel")}
                  </p>
                  <p className="mt-1 text-[12.5px] leading-5 text-[var(--color-muted)] text-pretty">
                    {t("landing.worksWithSection.protocolsBody")}
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 md:justify-end">
                {protocols.map((protocol) => (
                  <span
                    key={protocol.name}
                    className="inline-flex min-h-9 items-center rounded-[var(--radius-md)] border border-[var(--color-line-strong)] bg-[var(--color-primary-soft)]/65 px-3 text-[12.5px] font-bold text-[var(--color-primary-deep)]"
                  >
                    {protocol.name}
                  </span>
                ))}
              </div>
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
}
